import React, { useState } from "react";
import { Alert, Dialog, LoadingSkeleton, Tabs, Toggle } from "@narmi/design_system";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";
import { useSearchParams } from "react-router-dom-v5-compat";
import { useNotificationContext } from "cerulean";
import HistoryTable from "./HistoryTable";
import useUserConfiguration from "./hooks/useUserConfiguration";
import useInstitutionSetting from "./hooks/useInstitutionSetting";
import styles from "./CustomSettingPage.module.css";

const SETTING_NAME = "OAO_ASK_ADDITIONAL_FIELDS";

type FieldOption = {
  name: string;
  description: string;
  category: string;
  label?: string;
};

type FieldValue = {
  name: string;
  value: boolean;
  category: string;
};

const CONSUMER_FIELDS: FieldOption[] = [
  {
    name: "Occupation",
    description:
      "Primary and joint applicants will be asked to add their occupation on the personal information section of the application.",
    category: "consumer",
  },
  {
    name: "Middle name",
    description:
      "Primary and joint applicants will be asked to add their middle name on the personal information section of the application. This field is optional and will be included in the full name display throughout the application.",
    category: "consumer",
  },
  {
    name: "E-sign checkbox",
    description:
      "Applicants will be asked to agree to e-sign terms and conditions on the 'your info' section of the application.",
    category: "consumer",
  },
  {
    name: "Employer",
    description:
      "Primary and joint applicants will be asked to add their employer on the personal information section of the application.",
    category: "consumer",
  },
  {
    name: "CAPTCHA",
    description:
      "Applicants will be asked to complete CAPTCHA on the personal information section of the application.",
    category: "consumer",
  },
  {
    name: "Beneficiaries address",
    description:
      "Ask for address in the beneficiaries form on CAO. This field will be saved in Narmi and passed to the beneficiary profile in the core.",
    category: "consumer",
  },
  {
    name: "Beneficiaries relationship",
    description:
      "Ask for beneficiary relationship in the beneficiaries form on CAO. Relationship codes must be defined in the Beneficiaries Relationship Codes institution setting.",
    category: "consumer",
  },
  {
    name: "Beneficiaries SSN",
    description:
      "Ask for SSN in the beneficiaries form on CAO. This field will be saved in Narmi and passed to the beneficiary profile in the core.",
    category: "consumer",
  },
  {
    name: "Beneficiaries phone number",
    description:
      "Ask for phone number in the beneficiaries form on CAO. This field will be saved in Narmi and passed to the beneficiary profile in the core.",
    category: "consumer",
  },
  {
    name: "Beneficiaries email",
    description:
      "Ask for email in the beneficiaries form on CAO. This field will be saved in Narmi and passed to the beneficiary profile in the core.",
    category: "consumer",
  },
  {
    name: "Language preference",
    description:
      "Staff-led consumer applicants will be asked to add their language preference on the personal information section of the application.",
    category: "consumer",
  },
  {
    name: "Alias name",
    description:
      "Staff-led consumer applicants will be asked for an alias name (e.g., maiden name, nickname, or previous name for check acceptance). This creates an Individual type name record in the core.",
    category: "consumer",
  },
  {
    name: "Require disclosures for SL applications",
    label: "Require disclosures for staff-led applications",
    description:
      'If enabled, the staff confirmation checkbox does not appear in the Disclosures step of the Staff-Led flow, and applicants must review and accept disclosures themselves ("Send to applicant" option).',
    category: "consumer",
  },
  {
    name: "Remove revocable trusts for staff-led applications",
    description:
      "If true, do not show revocable trusts as an option for staff-led applications when trusts is a supported specialty ownership type",
    category: "consumer",
  },
  {
    name: "Remove irrevocable trusts for staff-led applications",
    description:
      "If true, do not show irrevocable trusts as an option for staff-led applications when trusts is a supported specialty ownership type",
    category: "consumer",
  },
  {
    name: "Electronic funds transfer agreement checkbox",
    description:
      "If enabled, the 'Link your bank account' modal replaces the passive electronic funds transfer agreement text with a checkbox. Applicants must open the agreement link and check the box before they can link the account.",
    category: "consumer",
  },
  {
    name: "Courtesy pay disclosure required",
    description:
      "If enabled, applicants will be required to acknowledge the courtesy pay disclosure during the application process.",
    category: "consumer",
  },
  {
    name: "Courtesy pay disclosure link required",
    description:
      "Requires 'Courtesy pay disclosure required'. If enabled, the acknowledgement checkbox links to the courtesy pay disclosure, and applicants must open that link before they can check the box. Has no effect unless the Courtesy Pay Disclosure setting contains a markdown link.",
    category: "consumer",
  },
];
const BUSINESS_FIELDS: FieldOption[] = [
  {
    name: "Role",
    description:
      "Applicants will be asked to add their role on the ownership section of the application.",
    category: "business",
  },
  {
    name: "E-sign checkbox",
    description:
      "Applicants will be asked to agree to e-sign terms and conditions on the ownership section of the application.",
    category: "business",
  },
  {
    name: "Website",
    description:
      "Applicants will be asked to add their website on the ‘your business’ section of the application.",
    category: "business",
  },
  {
    name: "CAPTCHA",
    description:
      "Applicants will be asked to complete CAPTCHA on the welcome screen of the application.",
    category: "business",
  },
  {
    name: "Non-DBA Business Name",
    description: "Ask for business name for sole proprietorships if the user selects 'no' for DBA.",
    category: "business",
  },
  {
    name: "Short date of incorporation",
    description: "Request date of incorporations in MM/YYYY format",
    category: "business",
  },
  {
    name: "Remove Electronic Funds Transfer agreement text",
    description: "Remove EFT text from 'Link your bank account' modal ",
    category: "business",
  },
  {
    name: "Assumed Business Name for LLCs and Corporations",
    description: "Ask 'Assumed Business Name' field for LLCs and Corporations",
    category: "business",
  },
  {
    name: "Assumed Business Name for General Partnerships",
    description: "Ask 'Assumed Business Name' field for General Partnerships",
    category: "business",
  },
  {
    name: "Require disclosures for SL applications",
    label: "Require disclosures for staff-led applications",
    description:
      'If enabled, the staff confirmation checkbox does not appear in the Disclosures step of the Staff-Led flow, and applicants must review and accept disclosures themselves ("Send to applicant" option).',
    category: "business",
  },
  {
    name: "Communications consent",
    description:
      "Show a required checkbox on the Business welcome form for consent to account‑opening communications (must be checked to submit).",
    category: "business",
  },

  {
    name: "Marketing consent",
    description:
      "Show an optional marketing emails checkbox on the Business welcome form; store the user’s choice.",
    category: "business",
  },
  {
    name: "Electronic funds transfer agreement checkbox",
    description:
      "If enabled, the 'Link your bank account' modal replaces the passive electronic funds transfer agreement text with a checkbox. Applicants must open the agreement link and check the box before they can link the account.",
    category: "business",
  },
  {
    name: "Courtesy pay disclosure required",
    description:
      "If enabled, applicants will be required to acknowledge the courtesy pay disclosure during the application process.",
    category: "business",
  },
  {
    name: "Info only role",
    description:
      "Staff-led business applicants can be marked as info only on the ownership section. Info only cannot be combined with any other role.",
    category: "business",
  },
];
const HIDE_FOR_NON_NARMI = ["E-sign checkbox"];

const previewStyles = {
  variables: {
    light: {
      addedBackground: "#fff",
      removedBackground: "#fff",
      addedGutterBackground: "##fff",
      removedGutterBackground: "#f7f7f7",
      wordAddedBackground: "#37B374",
      wordRemovedBackground: "#D93B3B",
      emptyLineBackground: "#D93B3B",
    },
  },
};

// Several field names appear in both categories, so the pending key is scoped by category
const pendingKeyFor = (category: string, fieldName: string) => `${category}:${fieldName}`;

const labelForField = (category: string, fieldName: string) => {
  const fields = category === "business" ? BUSINESS_FIELDS : CONSUMER_FIELDS;
  const field = fields.find((f) => f.name.toLowerCase() === fieldName);
  return field?.label || field?.name || fieldName;
};

function AOAskAdditionalField() {
  const { setting, save } = useInstitutionSetting(SETTING_NAME);
  const [searchParams, setSearchParams] = useSearchParams();
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const selectedTab = Number(searchParams.get("tab")) || 0;
  const [oldPreviewValue, setOldPreviewValue] = React.useState("");
  const [newPreviewValue, setNewPreviewValue] = React.useState("");
  const [previewIsOpen, setPreviewIsOpen] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState("");
  const [historyVersion, setHistoryVersion] = React.useState(0);
  const { sendNotification } = useNotificationContext();

  const { currentUser } = useUserConfiguration();

  const settingArray: FieldValue[] = setting ? JSON.parse(setting.value) : [];

  const getValueForFieldName = (category: string, fieldName: string) =>
    settingArray.find((s) => s.category === category && s.name === fieldName)?.value ?? false;

  const updateValue = async (fieldName: string, category: string, value: boolean) => {
    // Validate cannot remove both irrevocable and revocable
    if (fieldName === "remove irrevocable trusts for staff-led applications") {
      const hideRevocable = settingArray.find(
        (s) =>
          s.category === "consumer" &&
          s.name === "remove revocable trusts for staff-led applications" &&
          s.value === true
      );
      if (hideRevocable && value) {
        setErrorMsg(
          "Removing both irrevocable and revocable trusts is not allowed. Please uncheck one of the options before proceeding."
        );
        return;
      }
    }
    if (fieldName === "remove revocable trusts for staff-led applications") {
      const hideIrrevocable = settingArray.find(
        (s) =>
          s.category === "consumer" &&
          s.name === "remove irrevocable trusts for staff-led applications" &&
          s.value === true
      );
      if (hideIrrevocable && value) {
        setErrorMsg(
          "Removing both irrevocable and revocable trusts is not allowed. Please uncheck one of the options before proceeding."
        );
        return;
      }
    }

    // Clear error message if conflict is resolved
    setErrorMsg("");

    const oldSettingIdx = settingArray.findIndex(
      (s) => s.category === category && s.name === fieldName
    );

    const newSettingArray = [...settingArray];
    let newSetting;
    if (oldSettingIdx < 0) {
      newSetting = {
        name: fieldName,
        value,
        category,
      };
      newSettingArray.push(newSetting);
    } else {
      newSetting = { ...settingArray[oldSettingIdx] };
      newSetting.value = value;
      newSettingArray[oldSettingIdx] = newSetting;
    }

    setPendingKey(pendingKeyFor(category, fieldName));
    try {
      // Updates to a setting require a note, so create an automatic one
      await save(
        JSON.stringify(newSettingArray),
        `[AUTOMATED] ${currentUser.email} updated ${fieldName} (${category}) to ${value}`
      );
      setHistoryVersion((version) => version + 1);
      sendNotification({
        type: "success",
        text: `${labelForField(category, fieldName)} (${category}) ${
          value ? "enabled" : "disabled"
        }.`,
      });
    } catch (e) {
      sendNotification({
        type: "negative",
        text: e instanceof Error ? e.message : "Failed to save setting.",
      });
    } finally {
      setPendingKey(null);
    }
  };

  const setOldAndNewPreviewValues = (oldValue: string, newValue: string) => {
    setOldPreviewValue(oldValue);
    setNewPreviewValue(newValue);
    setPreviewIsOpen(true);
  };

  return (
    <div className={`${styles.page} nds-typography`}>
      <div className="margin--bottom--m" style={{ display: "flex", alignItems: "center" }}>
        <h2 style={{ marginTop: "0.5rem" }}>Add a field to applications</h2>
      </div>
      <div className="padding--all--m">
        <div className="margin--y--m">
          <Alert
            kind="error"
            isActive={!!errorMsg}
            isDismissable
            onUserDismiss={() => {
              setErrorMsg("");
            }}
          >
            {errorMsg}
          </Alert>
        </div>
        <Tabs
          selectedIndex={selectedTab}
          onTabChange={(index) => {
            setSearchParams((params) => {
              const nextParams = new URLSearchParams(params);
              nextParams.set("tab", String(index));
              return nextParams;
            });
          }}
        >
          <Tabs.List>
            <Tabs.Tab key="consumer" label="Consumer Account Opening" tabId="consumer" />
            <Tabs.Tab key="business" label="Business Account Opening" tabId="business" />
            <Tabs.Tab key="change-log" label="Change log" tabId="change-log" />
          </Tabs.List>
          <Tabs.Panel tabId="consumer">
            {!setting ? (
              <LoadingSkeleton isLoading />
            ) : (
              <div className={styles.toggleGrid}>
                {CONSUMER_FIELDS.filter(
                  // Hide certain toggles for non-Narmi users
                  (f) => !HIDE_FOR_NON_NARMI.includes(f.name) || currentUser.is_narmi
                )
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((field) => (
                    <AdditionalFieldToggle
                      key={`${field.name.toLowerCase()}_consumer`}
                      field={field}
                      value={getValueForFieldName("consumer", field.name.toLowerCase())}
                      pendingKey={pendingKey}
                      updateValue={updateValue}
                    />
                  ))}
              </div>
            )}
          </Tabs.Panel>
          <Tabs.Panel tabId="business">
            {!setting ? (
              <LoadingSkeleton isLoading />
            ) : (
              <div className={styles.toggleGrid}>
                {BUSINESS_FIELDS.sort((a, b) => a.name.localeCompare(b.name)).map((field) => (
                  <AdditionalFieldToggle
                    key={`${field.name.toLowerCase()}_business`}
                    field={field}
                    value={getValueForFieldName("business", field.name.toLowerCase())}
                    pendingKey={pendingKey}
                    updateValue={updateValue}
                  />
                ))}
              </div>
            )}
          </Tabs.Panel>
          <Tabs.Panel tabId="change-log">
            <HistoryTable
              key={historyVersion}
              settingName={SETTING_NAME}
              setOldAndNewPreviewValues={setOldAndNewPreviewValues}
              sendNotification={sendNotification}
              setIsHistoryDisplayed={() => {}} // no-op since this component doesn't manage history display state
            />
            <Dialog
              isOpen={previewIsOpen}
              title="Preview"
              onUserDismiss={() => setPreviewIsOpen(false)}
              width="100%"
            >
              <div>
                <ReactDiffViewer
                  oldValue={oldPreviewValue}
                  newValue={newPreviewValue}
                  splitView
                  showDiffOnly={false}
                  styles={previewStyles}
                  compareMethod={DiffMethod.WORDS}
                  leftTitle="Current value"
                  rightTitle="New value"
                />
              </div>
            </Dialog>
          </Tabs.Panel>
        </Tabs>
      </div>
    </div>
  );
}

function AdditionalFieldToggle({
  field,
  value,
  pendingKey,
  updateValue,
}: {
  field: FieldOption;
  value: boolean;
  pendingKey: string | null;
  updateValue: (fieldName: string, category: string, nextValue: boolean) => void;
}) {
  const { permissions } = useUserConfiguration();
  const canEditSettings = permissions.includes("backoffice.change_settings");
  // Build a single whitespace-free id so it is a valid `aria-labelledby` IDREF.
  // `field.name` contains spaces, which would otherwise tokenize into multiple
  // (nonexistent) ids and break the accessible label.
  const labelId = encodeURIComponent(`${field.name}-${field.category}`);
  const key = pendingKeyFor(field.category, field.name.toLowerCase());
  return (
    <div
      data-testid="additional-field-container"
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <div
        style={{
          display: "flex",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div id={labelId} className="margin--bottom--xxs fontWeight--semibold">
            {field.label || field.name}
          </div>
          <div
            className="margin--bottom--s"
            style={{
              color: "var(--color-mediumGrey)",
            }}
          >
            {field.description}
          </div>
        </div>
        <div>
          <Toggle
            labelledBy={labelId}
            onChange={(next) => {
              updateValue(field.name.toLowerCase(), field.category, next);
            }}
            isActive={value}
            isLoading={pendingKey === key}
            disabled={(pendingKey !== null && pendingKey !== key) || !canEditSettings}
          />
        </div>
      </div>
      <hr className="margin--top--m" style={{ marginLeft: 0, marginRight: 0 }} />
    </div>
  );
}

export default AOAskAdditionalField;
